import React, { useState } from 'react';
import { LinkIcon, ClipboardDocumentIcon, CheckIcon, EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

interface CustomerRegistrationLinkGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
}

const CustomerRegistrationLinkGenerator: React.FC<CustomerRegistrationLinkGeneratorProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    customMessage: '',
    fromName: user?.displayName || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const registrationUrl = `${window.location.origin}/register/${user?.organizationId}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = registrationUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendRegistrationEmail = async () => {
    if (!emailData.to.trim()) {
      setError('Email cím megadása kötelező');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const sendRegistrationLink = httpsCallable(functions, 'sendRegistrationLink');
      
      await sendRegistrationLink({
        to: emailData.to,
        organizationId: user?.organizationId,
        organizationName: 'YouCRM',
        fromName: emailData.fromName || user?.displayName,
        customMessage: emailData.customMessage
      });

      setEmailSent(true);
      setShowEmailForm(false);
      setEmailData({ to: '', customMessage: '', fromName: user?.displayName || '' });
      
      setTimeout(() => setEmailSent(false), 5000);
    } catch (error: any) {
      console.error('Error sending registration email:', error);
      setError(error.message || 'Hiba történt az email küldése során');
    } finally {
      setIsSubmitting(false);
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Ügyfél regisztráció');
    const body = encodeURIComponent(`
Kedves Partnerünk!

Kérjük, töltse ki az alábbi regisztrációs űrlapot, hogy felvehessük Önt ügyfeleink közé:

${registrationUrl}

Az űrlap kitöltése után hamarosan felvesszük Önnel a kapcsolatot.

Üdvözlettel,
${user?.displayName || 'A csapat'}
    `);
    
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ügyfél Regisztrációs Link" size="lg">
      <div className="p-6 space-y-6">
        {emailSent && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
            <div className="flex items-center">
              <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
              <span className="text-green-800 dark:text-green-200 font-medium">
                Email sikeresen elküldve!
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        <div className="text-center">
          <LinkIcon className="h-12 w-12 text-primary-600 dark:text-primary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Regisztrációs Link Generálva
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Ossza meg ezt a linket potenciális ügyfeleivel, hogy regisztrálhassanak a rendszerbe.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Regisztrációs URL
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={registrationUrl}
                readOnly
                className="flex-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 focus:outline-none"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
                className="flex-shrink-0"
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Másolva!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                    Másolás
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {!showEmailForm ? (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Hogyan működik?</h4>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li>• Az ügyfél megnyitja a linket</li>
                <li>• Kitölti a regisztrációs űrlapot</li>
                <li>• Automatikusan létrejön az ügyfél a rendszerben</li>
                <li>• Email értesítést kap mindkét fél</li>
              </ul>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailForm(true)}
                  className="flex items-center"
                >
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  Email küldés
                </Button>
                <Button
                  variant="outline"
                  onClick={shareViaEmail}
                  className="flex items-center"
                >
                  <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Mailto link
                </Button>
              </div>
              
              <Button variant="outline" onClick={onClose}>
                Bezárás
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Email küldése</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Címzett email címe *
                  </label>
                  <input
                    type="email"
                    value={emailData.to}
                    onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="pelda@email.com"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feladó neve
                  </label>
                  <input
                    type="text"
                    value={emailData.fromName}
                    onChange={(e) => setEmailData(prev => ({ ...prev, fromName: e.target.value }))}
                    placeholder="Az Ön neve"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Egyéni üzenet (opcionális)
                  </label>
                  <textarea
                    value={emailData.customMessage}
                    onChange={(e) => setEmailData(prev => ({ ...prev, customMessage: e.target.value }))}
                    placeholder="Kérjük, regisztráljon az alábbi linken keresztül..."
                    rows={3}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => setShowEmailForm(false)}
              >
                Vissza
              </Button>
              
              <div className="space-x-3">
                <Button
                  variant="primary"
                  onClick={sendRegistrationEmail}
                  disabled={isSubmitting || !emailData.to.trim()}
                  className="flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                      Küldés...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                      Email küldés
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CustomerRegistrationLinkGenerator;
